import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Share2, Mail, MessageSquare, Link as LinkIcon, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useGenerateShareLink } from "@/hooks/useGenerateShareLink";

interface SharePrescriptionModalProps {
  open: boolean;
  onClose: () => void;
  patient: {
    full_name: string;
    email?: string;
    celular?: string;
  };
  prescriptionItems: Array<{
    prescription: string;
    dosage?: string;
    duration?: string;
  }>;
  professionalName: string;
  professionalCpfCrmCrn?: string;
  patientId: string;
  onDownloadPDF: () => void;
}

export function SharePrescriptionModal({
  open,
  onClose,
  patient,
  prescriptionItems,
  professionalName,
  professionalCpfCrmCrn,
  patientId,
  onDownloadPDF,
}: SharePrescriptionModalProps) {
  const { generateLink, isGenerating } = useGenerateShareLink();
  const [shareLink, setShareLink] = useState<string>("");
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Build WhatsApp message
  const buildWhatsAppMessage = () => {
    let message = `Olá ${patient.full_name}! 👋\n\nSegue sua prescrição médica.\n\n`;
    message += `📋 *MEDICAMENTOS PRESCRITOS*\n`;
    
    prescriptionItems.forEach((item, index) => {
      message += `${index + 1}. ${item.prescription}`;
      if (item.dosage) message += ` - ${item.dosage}`;
      if (item.duration) message += ` por ${item.duration}`;
      message += `\n`;
    });
    
    message += `\n⏰ *LEMBRETES CONFIGURADOS*\n`;
    message += `Você receberá notificações para tomar seus medicamentos!\n\n`;
    message += `📎 Receita em PDF em anexo.\n\n`;
    message += `${professionalName}`;
    if (professionalCpfCrmCrn) message += ` - ${professionalCpfCrmCrn}`;
    
    return message;
  };

  const [whatsappMessage, setWhatsappMessage] = useState(buildWhatsAppMessage());

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(whatsappMessage);
    let phone = patient.celular?.replace(/\D/g, "");
    
    // Adicionar código do país (55) se não estiver presente
    if (phone && !phone.startsWith("55")) {
      phone = `55${phone}`;
    }
    
    const whatsappUrl = phone
      ? `https://wa.me/${phone}?text=${message}`
      : `https://wa.me/?text=${message}`;
    
    // Download PDF first
    onDownloadPDF();
    
    // Open WhatsApp
    window.open(whatsappUrl, "_blank");
    
    toast.success("WhatsApp aberto! Anexe o PDF baixado na conversa.", { duration: 5000 });
  };

  const handleEmailShare = async () => {
    if (!patient.email) {
      toast.error("Paciente não possui e-mail cadastrado");
      return;
    }

    toast.info("Funcionalidade de envio por e-mail será implementada em breve");
    // TODO: Implementar edge function para envio de e-mail via Resend
  };

  const handleGenerateLink = async () => {
    try {
      const link = await generateLink(patientId);
      setShareLink(link);
      toast.success("Link gerado com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar link:", error);
    }
  };

  const handleCopyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Share2 className="h-5 w-5 text-primary" />
            Compartilhar Prescrição
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* WhatsApp Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              <Label className="text-base font-semibold text-foreground">WhatsApp</Label>
            </div>
            <Textarea
              value={whatsappMessage}
              onChange={(e) => setWhatsappMessage(e.target.value)}
              className="min-h-[200px] font-mono text-sm bg-background border-border text-foreground"
              placeholder="Mensagem personalizada..."
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => setWhatsappMessage(buildWhatsAppMessage())}
                variant="outline"
                className="flex-1 border-border text-foreground hover:bg-muted"
              >
                Restaurar Mensagem
              </Button>
              <Button
                onClick={handleWhatsAppShare}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Enviar via WhatsApp
              </Button>
            </div>
          </div>

          {/* Email Section */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <Label className="text-base font-semibold text-foreground">E-mail</Label>
            </div>
            {patient.email ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Enviar prescrição para: <span className="font-medium text-foreground">{patient.email}</span>
                </p>
                <Button
                  onClick={handleEmailShare}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar por E-mail
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Paciente não possui e-mail cadastrado
              </p>
            )}
          </div>

          {/* Link Section */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-purple-600" />
              <Label className="text-base font-semibold text-foreground">Link de Acesso</Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Gere um link temporário para o paciente acessar sua prescrição online
            </p>
            
            {!shareLink ? (
              <Button
                onClick={handleGenerateLink}
                disabled={isGenerating}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                {isGenerating ? "Gerando..." : "Gerar Link Compartilhável"}
              </Button>
            ) : (
              <div className="space-y-2">
                <Label className="text-sm text-foreground">Link gerado:</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={shareLink}
                    readOnly
                    className="flex-1 bg-muted border-border text-foreground"
                  />
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    className="w-full sm:w-auto border-border text-foreground hover:bg-muted"
                  >
                    {linkCopied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  ⏱️ Este link expira em 24 horas
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
